import ExcelJS from 'exceljs';

export interface ParsedTabularFile {
    headers: string[];
    rows: Array<Record<string, string>>;
}

const SUPPORTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

export function isSupportedImportFile(file: File): boolean {
    const name = file.name.toLowerCase();
    return SUPPORTED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function cellText(value: ExcelJS.CellValue): string {
    if (value == null) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value).trim();
    }
    if (value instanceof Date) return value.toISOString();
    if (typeof value === 'object') {
        if ('text' in value && typeof value.text === 'string') return value.text.trim();
        if ('result' in value) return cellText((value as { result: ExcelJS.CellValue }).result);
        if ('richText' in value && Array.isArray(value.richText)) {
            return value.richText.map((r) => r.text).join('').trim();
        }
    }
    return String(value).trim();
}

function uniqueHeaders(raw: string[]): string[] {
    const seen = new Map<string, number>();
    return raw.map((h, i) => {
        const base = h.trim() || `Column ${i + 1}`;
        const count = seen.get(base) ?? 0;
        seen.set(base, count + 1);
        return count === 0 ? base : `${base} (${count + 1})`;
    });
}

function parseCsvText(text: string): ParsedTabularFile {
    const lines = text
        .replace(/^\uFEFF/, '')
        .split(/\r\n|\n|\r/)
        .filter((line) => line.length > 0);
    if (lines.length === 0) return { headers: [], rows: [] };

    const parseLine = (line: string): string[] => {
        const cells: string[] = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (inQuotes) {
                if (ch === '"') {
                    if (line[i + 1] === '"') {
                        current += '"';
                        i += 1;
                    } else {
                        inQuotes = false;
                    }
                } else {
                    current += ch;
                }
            } else if (ch === '"') {
                inQuotes = true;
            } else if (ch === ',') {
                cells.push(current.trim());
                current = '';
            } else {
                current += ch;
            }
        }
        cells.push(current.trim());
        return cells;
    };

    const headers = uniqueHeaders(parseLine(lines[0]));
    const rows: Array<Record<string, string>> = [];
    for (let r = 1; r < lines.length; r++) {
        const cells = parseLine(lines[r]);
        const record: Record<string, string> = {};
        let anyValue = false;
        headers.forEach((header, idx) => {
            const value = (cells[idx] ?? '').trim();
            record[header] = value;
            if (value) anyValue = true;
        });
        if (anyValue) rows.push(record);
    }
    return { headers, rows };
}

function sheetToParsed(sheet: ExcelJS.Worksheet): ParsedTabularFile {
    const headerRow = sheet.getRow(1);
    const rawHeaders: string[] = [];
    headerRow.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        rawHeaders[colNumber - 1] = cellText(cell.value);
    });
    while (rawHeaders.length && !rawHeaders[rawHeaders.length - 1]) {
        rawHeaders.pop();
    }
    const headers = uniqueHeaders(rawHeaders);
    const rows: Array<Record<string, string>> = [];
    sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber === 1) return;
        const record: Record<string, string> = {};
        let anyValue = false;
        headers.forEach((header, idx) => {
            const value = cellText(row.getCell(idx + 1).value);
            record[header] = value;
            if (value) anyValue = true;
        });
        if (anyValue) rows.push(record);
    });
    return { headers, rows };
}

/**
 * Parse CSV / Excel into headers + row objects keyed by header name.
 */
export async function parseTabularFile(file: File): Promise<ParsedTabularFile> {
    if (!isSupportedImportFile(file)) {
        throw new Error('Only CSV and Excel (.csv, .xlsx, .xls) files are supported.');
    }

    const name = file.name.toLowerCase();
    if (name.endsWith('.csv')) {
        return parseCsvText(await file.text());
    }

    const buffer = await file.arrayBuffer();
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(buffer);
    const sheet = workbook.worksheets[0];
    if (!sheet) return { headers: [], rows: [] };
    return sheetToParsed(sheet);
}

/**
 * Apply column mapping: for each target field, take the first non-empty mapped source column.
 * For `unionFieldIds`, join all non-empty mapped source cells (comma-separated) instead.
 * Constant values fill required fields with no mapped columns.
 */
export function applyColumnMapping(
    rows: Array<Record<string, string>>,
    mapping: Record<string, string[]>,
    constants: Record<string, string>,
    targetFieldIds: string[],
    unionFieldIds: ReadonlySet<string> = new Set(),
): Array<Record<string, string>> {
    return rows.map((row) => {
        const out: Record<string, string> = {};
        targetFieldIds.forEach((fieldId) => {
            const sources = mapping[fieldId] ?? [];
            let value = '';
            if (unionFieldIds.has(fieldId)) {
                const parts: string[] = [];
                for (const source of sources) {
                    const cell = (row[source] ?? '').trim();
                    if (cell) parts.push(cell);
                }
                value = parts.join(', ');
            } else {
                for (const source of sources) {
                    const cell = (row[source] ?? '').trim();
                    if (cell) {
                        value = cell;
                        break;
                    }
                }
            }
            if (!value && constants[fieldId] != null && constants[fieldId] !== '') {
                value = constants[fieldId];
            }
            out[fieldId] = value;
        });
        return out;
    });
}
